if (!customElements.get('video-player')) {
  class VideoPlayer extends HTMLElement {
    connectedCallback() {
      this.media = this.querySelector('.video-player__media');
      this.controlsEl = this.querySelector('[data-video-player-controls]');
      this.timeEl = this.querySelector('[data-video-player-time]');
      this.fillEl = this.querySelector('[data-video-player-fill]');
      this.seekEl = this.querySelector('[data-video-player-seek]');
      this.scrubberEl = this.querySelector('[data-video-player-scrubber]');
      this.isScrubbing = false;
      this.hideTimer = null;

      this.tryBind();
      if (!this.video) {
        this.observer = new MutationObserver(() => this.tryBind());
        this.observer.observe(this, { childList: true, subtree: true });
      }
    }

    tryBind() {
      if (this.video) return;
      const v = this.querySelector('video');
      if (!v) return;
      this.video = v;
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.bindVideo();
      this.bindControls();
      this.bindAutoHide();
      this.bindMediaClick();

      this.dataset.muted = String(this.video.muted);
      this.dataset.state = this.video.paused ? 'paused' : 'playing';
      this.dataset.controls = 'visible';
      this.setAttribute('data-ready', '');
      this.updateTime();
    }

    bindVideo() {
      this.video.addEventListener('play', () => {
        this.dataset.state = 'playing';
        this.scheduleHide();
      });
      this.video.addEventListener('pause', () => {
        this.dataset.state = 'paused';
        this.show();
      });
      this.video.addEventListener('ended', () => {
        this.dataset.state = 'paused';
        this.show();
      });
      this.video.addEventListener('volumechange', () => {
        this.dataset.muted = String(this.video.muted);
      });
      this.video.addEventListener('timeupdate', () => this.updateTime());
      this.video.addEventListener('loadedmetadata', () => this.updateTime());
    }

    bindControls() {
      if (!this.controlsEl) return;
      this.controlsEl.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-action]');
        if (!btn) return;
        event.stopPropagation();
        switch (btn.dataset.action) {
          case 'play-pause': this.togglePlay(); break;
          case 'mute': this.toggleMute(); break;
          case 'fullscreen': this.enterFullscreen(); break;
        }
      });

      if (this.seekEl) {
        this.seekEl.addEventListener('pointerdown', () => { this.isScrubbing = true; });
        this.seekEl.addEventListener('input', () => {
          if (!this.video || !isFinite(this.video.duration)) return;
          this.isScrubbing = true;
          const pct = parseFloat(this.seekEl.value) / 1000;
          this.video.currentTime = pct * this.video.duration;
          this.updateTime();
          this.show();
        });
        this.seekEl.addEventListener('change', () => { this.isScrubbing = false; });
        this.seekEl.addEventListener('pointerup', () => { this.isScrubbing = false; });
        this.seekEl.addEventListener('click', (event) => event.stopPropagation());
      }
    }

    bindAutoHide() {
      const showOnInteract = () => this.show();
      this.addEventListener('mousemove', showOnInteract);
      this.addEventListener('mouseenter', showOnInteract);
      this.addEventListener('focusin', showOnInteract);
      this.addEventListener('touchstart', showOnInteract, { passive: true });
      this.addEventListener('mouseleave', () => {
        if (this.video && !this.video.paused && !this.isScrubbing) {
          this.dataset.controls = 'hidden';
          clearTimeout(this.hideTimer);
        }
      });
    }

    bindMediaClick() {
      if (!this.media) return;
      this.media.addEventListener('click', (event) => {
        if (event.target.closest('[data-video-player-controls]')) return;
        if (event.target.closest('.deferred-media__poster')) return;
        if (event.target.closest('button, [data-action]')) return;
        this.togglePlay();
      });
    }

    show() {
      this.dataset.controls = 'visible';
      clearTimeout(this.hideTimer);
      this.scheduleHide();
    }

    scheduleHide() {
      clearTimeout(this.hideTimer);
      if (!this.video || this.video.paused) return;
      this.hideTimer = setTimeout(() => {
        if (this.video && !this.video.paused && !this.isScrubbing) {
          this.dataset.controls = 'hidden';
        }
      }, 2500);
    }

    togglePlay() {
      if (!this.video) return;
      if (this.video.paused) {
        const p = this.video.play();
        if (p && p.catch) p.catch(() => {});
      } else {
        this.video.pause();
      }
    }

    toggleMute() {
      if (!this.video) return;
      this.video.muted = !this.video.muted;
    }

    enterFullscreen() {
      if (!this.video) return;
      const fn = this.video.requestFullscreen
        || this.video.webkitRequestFullscreen
        || this.video.webkitEnterFullscreen
        || this.video.msRequestFullscreen;
      if (fn) {
        try { fn.call(this.video); } catch (e) { /* no-op */ }
      }
    }

    updateTime() {
      if (!this.video) return;
      const dur = this.video.duration;
      if (!isFinite(dur) || dur <= 0) return;
      const cur = this.video.currentTime;
      const remaining = Math.max(0, dur - cur);
      const min = Math.floor(remaining / 60);
      const sec = Math.floor(remaining % 60).toString().padStart(2, '0');
      if (this.timeEl) this.timeEl.textContent = `-${min}:${sec}`;

      const pct = (cur / dur) * 100;
      if (this.fillEl) this.fillEl.style.width = `${pct}%`;
      if (this.seekEl && !this.isScrubbing) {
        this.seekEl.value = Math.round(pct * 10);
      }
    }
  }
  customElements.define('video-player', VideoPlayer);
}
